/**
 * Trocar a minha senha, de dentro do painel.
 *
 * Pedido do Cristiano, de 18/10/2026. A pessoa já entrou: ela digita a senha de agora, a nova duas
 * vezes, e o número do aplicativo quando a conta tem segundo fator. **Esta sessão fica aberta**; as
 * outras (o computador da secretaria, o celular emprestado) caem na hora.
 *
 * Para quem entra com a senha do notebook, a troca vale nos dois lugares: aqui na hora, e nos
 * notebooks do Campo na próxima sincronização. A tela diz isso no fim, porque é a frase que evita
 * o telefonema de "troquei e no computador da tesouraria não funciona".
 *
 * Uma coluna só e os campos na ordem em que a pessoa pensa, que é a mesma do segundo fator: quem
 * está do outro lado pode estar no celular.
 */
import { useState, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor, servidor, type Eu, type SenhaTrocadaNoPainel } from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'

export function MinhaSenha({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [atual, setAtual] = useState('')
  const [nova, setNova] = useState('')
  const [deNovo, setDeNovo] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [feita, setFeita] = useState<SenhaTrocadaNoPainel | null>(null)

  async function trocar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)

    // A conferência das duas digitações é da tela: o servidor nem fica sabendo da segunda.
    if (nova !== deNovo) {
      setErro('As duas senhas novas não são iguais. Digite de novo.')
      return
    }

    setOcupado(true)
    try {
      setFeita(await servidor.trocarAMinhaSenha(atual, nova, eu.tem_segundo_fator ? codigo.trim() : undefined))
      setAtual('')
      setNova('')
      setDeNovo('')
      setCodigo('')
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível trocar a senha.')
      // O código é de uso único: errado ou certo, o que foi digitado não serve mais.
      setCodigo('')
    } finally {
      setOcupado(false)
    }
  }

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto="sim">
        <h1>Minha senha</h1>

        {eu.treinamento ? (
          <section className="passo">
            <p className="explicacao">
              Dentro do treinamento a senha não se troca. Volte ao Campo e troque por lá.
            </p>
          </section>
        ) : feita !== null ? (
          <section className="passo">
            <p className="recado feito" role="status">Pronto. A sua senha foi trocada.</p>

            {feita.vale_no_notebook && (
              <p className="explicacao">
                Ela já vale aqui no painel. No computador da tesouraria, ela passa a valer na
                próxima vez que ele se conectar à internet e receber as novidades. Até lá, lá
                continua valendo a senha de antes.
              </p>
            )}

            <p className="explicacao">
              {feita.outras_sessoes_encerradas === 0
                ? 'Não havia o painel aberto com a sua conta em outro lugar.'
                : feita.outras_sessoes_encerradas === 1
                  ? 'O painel que estava aberto com a sua conta em outro lugar foi fechado.'
                  : `Os ${feita.outras_sessoes_encerradas} painéis que estavam abertos com a sua conta em outros lugares foram fechados.`}{' '}
              Este continua aberto.
            </p>

            {feita.aviso_por_email && (
              <p className="explicacao miudo">
                Um aviso da troca foi mandado para o seu e-mail. Se um dia chegar um aviso desses
                sem você ter trocado, use "Esqueci minha senha" na tela de entrar.
              </p>
            )}

            <button className="principal" type="button" onClick={() => window.location.assign('/')}>
              Voltar ao Início
            </button>
          </section>
        ) : (
          <section className="passo">
            <p className="explicacao">
              {eu.entra_com_a_senha_do_notebook
                ? 'É a mesma senha do computador da tesouraria. Trocando aqui, ela muda nos dois lugares.'
                : 'É a senha com que você entra neste painel.'}
            </p>

            {erro !== null && <p className="recado" role="alert">{erro}</p>}

            <form onSubmit={trocar} className="confirmar-codigo">
              <label>
                Senha de agora
                <input
                  type="password"
                  name="senha-atual"
                  value={atual}
                  onChange={(e) => setAtual(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  required
                />
              </label>

              <label>
                Senha nova
                <input
                  type="password"
                  name="senha-nova"
                  value={nova}
                  onChange={(e) => setNova(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              <label>
                Digite a senha nova outra vez
                <input
                  type="password"
                  name="senha-de-novo"
                  value={deNovo}
                  onChange={(e) => setDeNovo(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              {eu.tem_segundo_fator && (
                <label>
                  Número do aplicativo do celular
                  <input
                    type="text"
                    className="codigo"
                    name="codigo"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    maxLength={20}
                    required
                  />
                </label>
              )}

              <button className="principal" type="submit" disabled={ocupado}>
                {ocupado ? 'Trocando...' : 'Trocar a senha'}
              </button>
            </form>

            <p className="explicacao miudo">
              De 6 a 20 caracteres, com pelo menos uma letra e um número. É a mesma regra de
              sempre.
              {eu.tem_segundo_fator && (
                <> Sem o celular, um código de emergência serve no lugar do número.</>
              )}
            </p>
            <p className="explicacao miudo">
              Se o painel estiver aberto com a sua conta em outro computador ou celular, ele fecha
              na hora. Este continua aberto.
            </p>
          </section>
        )}
      </main>
    </>
  )
}
